/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import apiClient from '@/apiClient'
import type {
  CreateBillBody,
  BillsList,
  Bill,
  PaymentMethod,
  OverpayPolicy,
} from '@/types/backend'
import type { CartDraft } from '@/types/cart'
import { useNavigate } from 'react-router-dom'

function normalizeBillsList(wire: any): BillsList {
  if (wire && 'items' in wire && 'pagination' in wire) return wire
  // format legacy { data, total, page, pageSize }
  const pageCount = wire?.pageSize
    ? Math.max(1, Math.ceil((wire.total ?? 0) / wire.pageSize))
    : 1
  return {
    items: wire?.data ?? [],
    pagination: {
      page: wire?.page ?? 1,
      pageSize: wire?.pageSize ?? (wire?.data?.length || 20),
      total: wire?.total ?? 0,
      pageCount,
    },
  }
}

// applyExistingCredit n'est pas encore dans le schéma OpenAPI généré
// (types:api régénère depuis /docs-json) — étendu localement en attendant.
export type CreateBillBodyWithCredit = CreateBillBody & {
  applyExistingCredit?: boolean
}

export function toCreateBillBody(
  cart: CartDraft,
  applyExistingCredit?: boolean
): CreateBillBodyWithCredit {
  if (!cart.customerId) {
    throw new Error('Client requis')
  }
  return {
    customerId: cart.customerId,
    items: cart.items.map((it) => ({
      name: it.name,
      unitPrice: Number(it.unitPrice),
      quantity: Number(it.quantity),
      category: it.category,
      packaging: it.packaging,
      itemId: it.itemId,
    })),
    tax: Number(cart.tax || 0),
    incurredOn: cart.incurredOn,
    notes: cart.notes,
    initialPaymentAmount: cart.initialPaymentAmount,
    initialPaymentMethod: cart.initialPaymentMethod as
      | PaymentMethod
      | undefined,
    initialOverpayPolicy: (cart.initialOverpayPolicy ??
      'APPLY_TO_DEBT') as OverpayPolicy,
    applyExistingCredit,
  }
}

export const useCreateBillMutation = () => {
  const qc = useQueryClient()
  const navigate = useNavigate()
  return useMutation({
    mutationFn: async (vars: {
      cart: CartDraft
      applyExistingCredit?: boolean
    }) => {
      const payload = toCreateBillBody(vars.cart, vars.applyExistingCredit)
      const { data } = await apiClient.post<Bill>(`/bills`, payload)
      return data
    },
    onSuccess: (res) => {
      const billId = res?.id
      if (billId) {
        navigate(`/bills/${billId}?action=print&back=/dashboard`)
      }
      qc.invalidateQueries({ queryKey: ['bills'] })
      qc.invalidateQueries({ queryKey: ['customers'] })
    },
  })
}

export const useBillsQuery = (params: {
  customerId?: string
  status?: Bill['status'] // "ISSUED" | "PARTIALLY_PAID" | "PAID" | "VOID"
  from?: string
  to?: string
  q?: string
  page: number
  pageSize: number
}) =>
  useQuery({
    queryKey: ['bills', 'list', params],
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const { data } = await apiClient.get<any>('/bills', { params })
      return normalizeBillsList(data)
    },
  })

export const useBillQuery = (id?: string, enabled = !!id) =>
  useQuery({
    queryKey: ['bills', 'detail', id],
    enabled,
    queryFn: async () => {
      const { data } = await apiClient.get<Bill>(`/bills/${id}`)
      return data
    },
  })

export const useUpdateBillMetaMutation = (id: string) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (payload: { incurredOn?: string; note?: string }) => {
      const { data } = await apiClient.patch<Bill>(`/bills/${id}`, payload)
      return data
    },
    onSuccess: (updated) => {
      qc.setQueryData(['bills', 'detail', id], updated)
      qc.invalidateQueries({ queryKey: ['bills'] })
    },
  })
}

export const useVoidBillMutation = (id: string) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (payload?: { reason?: string }) => {
      const { data } = await apiClient.post<Bill>(`/bills/${id}/void`, payload)
      return data
    },
    onSuccess: (updated) => {
      qc.setQueryData(['bills', 'detail', id], updated)
      qc.invalidateQueries({ queryKey: ['bills'] })
      qc.invalidateQueries({ queryKey: ['payments'] })
      qc.invalidateQueries({ queryKey: ['items'] })
    },
  })
}
